import {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';

/**
 * TenantThemeProvider — architecture-ready slot for per-tenant overrides.
 *
 * Per `feedback_design_decisions_2026-05-24.md` #4:
 *   MVP ships logo-only white-label. Full per-tenant token override is
 *   prepared so a future PR can flip the switch without refactoring
 *   components.
 *
 * Контракт (MVP):
 *   - <TenantThemeProvider tenantSlug="mara"> wraps the app at the root.
 *   - In MVP every tenant gets the default theme (D5b Final).
 *   - When `tenants.theme_overrides` JSONB starts populating real values
 *     in a later release, this component will:
 *       * fetch /v1/public/{tenant}/theme,
 *       * write CSS variables onto a style element OR inline on <html>,
 *       * still expose `useTenantTheme()` for component-side reads.
 *
 *   - <html data-theme="light"> is the default. Dark slot reserved
 *     ([data-theme="dark"]) but no palette ships yet.
 */

export type TenantThemeOverrides = Partial<{
  colorPrimary: string;
  colorPrimaryDarker: string;
  colorBgBase: string;
  colorBgBody: string;
  colorTextPrimary: string;
  fontDisplay: string;
  fontHeading: string;
  fontUi: string;
  logoUrl: string;
}>;

export interface TenantTheme {
  tenantSlug: string | null;
  overrides: TenantThemeOverrides;
  isDefault: boolean;
}

const DEFAULT_THEME: TenantTheme = {
  tenantSlug: null,
  overrides: {},
  isDefault: true,
};

const TenantThemeContext = createContext<TenantTheme>(DEFAULT_THEME);

export interface TenantThemeProviderProps {
  tenantSlug?: string | null;
  overrides?: TenantThemeOverrides;
  children: ReactNode;
}

export function TenantThemeProvider({
  tenantSlug = null,
  overrides,
  children,
}: TenantThemeProviderProps): JSX.Element {
  const value = useMemo<TenantTheme>(() => {
    // MVP: always default. Override map kept for future contract stability.
    const effective: TenantThemeOverrides = overrides ?? {};
    return {
      tenantSlug,
      overrides: effective,
      isDefault: Object.keys(effective).length === 0,
    };
  }, [tenantSlug, overrides]);

  return (
    <TenantThemeContext.Provider value={value}>
      {children}
    </TenantThemeContext.Provider>
  );
}

export function useTenantTheme(): TenantTheme {
  return useContext(TenantThemeContext);
}
